import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isBefore } from 'date-fns';
import { isArray, isFunction } from 'lodash-es';
import { array, number, object, string } from 'yup';
import classNames from '../../../../utils/classNames';
import { defaultPropTypes, useOverrideState } from '../../utils';
import FormFieldContainer, { BaseFormFieldContainer } from '../FormFieldContainer';
import { OverridableSelect } from '../../../form/OverridableSelect';
import TimePicker from '../../../form/TimePicker';
import { OverridableWrapper } from '../InputField';
import { useProfile } from '../../../../profile';

import { useFormSchema } from '../../../pages/measuringPoint/schema-utils';
import { SwarmType } from '../../../../enums';
import { timeToDate } from '../../../../utils/time';
import { GenericRowField, inlineFieldGroupClasses } from '../genericRowField/GenericRowField';
import Input from '../../../form/Input';

function useDailyValueSettingsDefault() {
    return useFormSchema(SwarmType.Sound, false).fields.getByName('dailyValueSettings').default;
}

const TIME_APPLICATION = {
    FULL: 'full',
    CUSTOM: 'custom',
};

function FormField({ name, title, children }) {
    return (
        <div className="text-form-text relative mr-5 inline-block w-[250px] list-none align-top">
            <div>
                <label htmlFor={`id_${name}`} className={`text-[11px]`}>
                    {title}
                </label>
            </div>
            {children}
        </div>
    );
}
FormField.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({
        timeSettings: PropTypes.shape({
            start: PropTypes.string,
            end: PropTypes.string,
        }).isRequired,
        triggers: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.shape({
        timeSettings: PropTypes.shape({
            end: PropTypes.shape({
                type: PropTypes.string,
            }),
        }),
        triggers: PropTypes.arrayOf(
            PropTypes.shape({
                trigger: PropTypes.shape({
                    message: PropTypes.string,
                }),
                days: PropTypes.shape({
                    message: PropTypes.string,
                }),
            })
        ),
    }),
};

function usePartialValue(value, onChange, key) {
    const partialValue = useMemo(() => value[key], [value, key]);
    const partialOnChange = useCallback(
        (newValueOrCallback) => {
            const newValue = isFunction(newValueOrCallback)
                ? newValueOrCallback(value[key])
                : newValueOrCallback;

            onChange({
                ...value,
                [key]: newValue,
            });
        },
        [value, onChange, key]
    );

    return [partialValue, partialOnChange];
}

const expectedErrors = {
    END_BEFORE_START_ERROR: 'END_BEFORE_START_ERROR',
};

function checkEndBeforeStartError(error) {
    return error?.timeSettings?.end?.type === expectedErrors.END_BEFORE_START_ERROR;
}

function hasTriggerError(error) {
    return isArray(error?.triggers);
}

function TimeInput({
    name,
    overrideName,
    label,
    disabled,
    value,
    onChange,
    error,
    use24HourNotation,
}) {
    return (
        <BaseFormFieldContainer name={name} label={label} inline={true}>
            <OverridableWrapper
                overrideName={overrideName}
                disabled={disabled}
                render={(props) => (
                    <TimePicker
                        id={`id_${name}`}
                        value={value}
                        is24Hour={use24HourNotation}
                        error={error}
                        onChange={onChange}
                        {...props}
                    />
                )}
            />
        </BaseFormFieldContainer>
    );
}
TimeInput.propTypes = {
    name: PropTypes.string.isRequired,
    overrideName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool.isRequired,
    use24HourNotation: PropTypes.bool.isRequired,
};

function TimeSettings({ name, value: valueIncoming, onChange: onChangeIncoming, disabled, error }) {
    const { t } = useTranslation();
    const { overridable, overridden } = useOverrideState(name);

    const [value, onChange] = usePartialValue(valueIncoming, onChangeIncoming, 'timeSettings');
    const timeApplicationOptions = useMemo(
        () => [
            [TIME_APPLICATION.FULL, t('24_HOURS')],
            [TIME_APPLICATION.CUSTOM, gettext('CUSTOM')],
        ],
        [t]
    );
    const [timeSettings, setTimeApplication] = useState(TIME_APPLICATION.FULL);
    const { use24HourNotation } = useProfile();
    const { timeSettings: defaultTimeSettings } = useDailyValueSettingsDefault();

    const hasEndBeforeStartError = useMemo(() => checkEndBeforeStartError(error), [error]);

    const errorMessage = useMemo(
        () => (hasEndBeforeStartError ? gettext('END_BEFORE_START_ERROR') : null),
        [hasEndBeforeStartError]
    );

    useEffect(() => {
        setTimeApplication(
            value.start === '00:00' && value.end === '23:59'
                ? TIME_APPLICATION.FULL
                : TIME_APPLICATION.CUSTOM
        );
    }, [value]);

    return (
        <BaseFormFieldContainer error={errorMessage}>
            <div className={inlineFieldGroupClasses}>
                <BaseFormFieldContainer
                    name="time_application"
                    label={t('TIME_APPLICATION')}
                    inline={true}
                >
                    <OverridableWrapper
                        overrideName={name}
                        disabled={disabled}
                        render={(props) => (
                            <OverridableSelect
                                id="id_time_application"
                                data={timeApplicationOptions}
                                value={timeSettings}
                                overridable={overridable}
                                overridden={overridden}
                                overrideName={name}
                                onChange={({ target: { value: newValue } }) => {
                                    if (newValue === TIME_APPLICATION.FULL) {
                                        // The default settings are a full day. So
                                        // we can set it back to default here.
                                        onChange((current) => ({
                                            ...current,
                                            ...defaultTimeSettings,
                                        }));
                                    }

                                    setTimeApplication(newValue);
                                }}
                                {...props}
                            />
                        )}
                    />
                </BaseFormFieldContainer>
                {timeSettings === TIME_APPLICATION.CUSTOM && (
                    <>
                        <TimeInput
                            name="start_time"
                            label={gettext('START_TIME')}
                            overrideName={name}
                            disabled={disabled}
                            error={hasEndBeforeStartError}
                            use24HourNotation={use24HourNotation}
                            value={value.start}
                            onChange={(newValue) => {
                                onChange((current) => ({
                                    ...current,
                                    start: newValue,
                                }));
                            }}
                        />
                        <TimeInput
                            name="end_time"
                            label={gettext('END_TIME')}
                            overrideName={name}
                            disabled={disabled}
                            error={hasEndBeforeStartError}
                            use24HourNotation={use24HourNotation}
                            value={value.end}
                            onChange={(newValue) => {
                                onChange((current) => ({
                                    ...current,
                                    end: newValue,
                                }));
                            }}
                        />
                    </>
                )}
            </div>
        </BaseFormFieldContainer>
    );
}
TimeSettings.propTypes = propTypes;

const emptyTriggerRow = { trigger: 0, days: 0 };

const minimumValue = 0;

function TriggerSettings({
    name,
    value: valueIncoming,
    onChange: onChangeIncoming,
    disabled,
    error,
}) {
    const [value, onChange] = usePartialValue(valueIncoming, onChangeIncoming, 'triggers');

    const fields = useMemo(
        () => [
            {
                name: 'trigger',
                label: gettext('EXCEEDANCE_TRIGGER_IN_DB'),
                render(props) {
                    return <Input {...props} type="number" min={minimumValue} />;
                },
            },
            {
                name: 'days',
                label: gettext('MAXIMUM_EXPOSURE_TIME_IN_DAYS'),
                render(props) {
                    return <Input {...props} type="number" min={minimumValue} />;
                },
            },
        ],
        []
    );

    return (
        <div className="flex flex-col gap-y-2.5">
            <h3 className="mb-3.5 text-black">{gettext('TRIGGER_SETTINGS')}</h3>

            <GenericRowField
                name={name}
                fields={fields}
                emptyRow={emptyTriggerRow}
                maxRows={5}
                value={value}
                error={error?.triggers}
                onChange={onChange}
                disabled={disabled}
            />
        </div>
    );
}
TriggerSettings.propTypes = propTypes;

function SoundDailyValueSettings(props) {
    return (
        <div className="flex flex-col gap-y-3.5">
            <TimeSettings {...props} />
            <TriggerSettings {...props} />
        </div>
    );
}
SoundDailyValueSettings.propTypes = propTypes;

function hasUnexpectedError(error) {
    return error && !checkEndBeforeStartError(error) && !hasTriggerError(error);
}

// Regular expression to validate the HH:MM time format
const TIME_FORMAT_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

function SoundDailyValueSettingsField({ fieldOptions }) {
    const { name } = fieldOptions;
    const { disabled } = useOverrideState(name);
    const defaultValue = useDailyValueSettingsDefault();

    return (
        <Controller
            name={name}
            defaultValue={defaultValue}
            rules={{
                buildValidationSchema() {
                    function triggerValidation() {
                        return number()
                            .typeError(gettext('NUMBER_TYPE_ERROR'))
                            .min(minimumValue, gettext('TRIGGER_OR_DAYS_NEGATIVE'))
                            .required(gettext('FIELD_REQUIRED_VALIDATION'));
                    }
                    function timeValidation() {
                        return string()
                            .required(gettext('FIELD_REQUIRED_VALIDATION'))
                            .matches(TIME_FORMAT_REGEX, gettext('TIME_FORMAT_INVALID'));
                    }

                    return object({
                        timeSettings: object().shape({
                            start: timeValidation(),
                            end: timeValidation().test(
                                expectedErrors.END_BEFORE_START_ERROR,
                                // String below is not presented to the user.
                                'End time must be after start time',
                                function validateStartBeforeEnd(value) {
                                    const { start } = this.parent;
                                    return !isBefore(timeToDate(value), timeToDate(start));
                                }
                            ),
                        }),
                        triggers: array()
                            .of(
                                object().shape({
                                    trigger: triggerValidation(),
                                    days: triggerValidation(),
                                })
                            )
                            .required(),
                    });
                },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormFieldContainer
                    fieldOptions={fieldOptions}
                    showLabel={false}
                    showError={hasUnexpectedError(error)}
                    className={classNames(
                        hasUnexpectedError(error) && 'rounded border border-red-500'
                    )}
                >
                    <SoundDailyValueSettings
                        name={name}
                        value={value}
                        error={error}
                        onChange={onChange}
                        disabled={disabled}
                    />
                </FormFieldContainer>
            )}
        />
    );
}
SoundDailyValueSettingsField.propTypes = defaultPropTypes;

export default SoundDailyValueSettingsField;
